/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import apiService from '../../../../share/util/api/service';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import session from '../../../../share/session';
import util from '../../../../share/util/utils';
import diagnoseResource from "../../../../share/util/diagnose";
import handler from '../../../../share/util/apiResultValidator';
import projectData from '../../../../share/components/vue/ProjectData';
import customTable from '../../../../share/components/vue/PaginationTable';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import shareVehicle from '../../../../share/util/vehicle';
import vinInfoUtils from "../../../../util/vinInfoUtils";

/** 各フェーズ定義 */
const PHASE = {
  LIST_VIEW: 'listView',
  PROJECT_DATA: 'projectData',
  FAILED: 'failed',
};

export default {
  name: 'requirement139',
  data() {
    return {
      phase: PHASE.LIST_VIEW,
      isLoading: false,
      systemId: '',
      i18nWsDidKey: '',
      displayInfo: [],
      requestId: '',
      tableColumns: [],                                                              // 一覧表示カラム情報
      vin: null,                                                                     // データ保存画面のVIN/車両番号
      vehicleName: null,                                                             // データ保存画面の車両
      displayProjectID: '',                                                          // データ保存画面のID
      hideRegistrationNumber: true,                                                  // データ保存画面の登録番号表示フラグ
      registrationNumber: null,                                                      // データ保存画面の登録番号表示
      functionName: 'individual.individual_diagnosis_function_work_support',         // データ保存画面の機能名
      dataName: '',                                                                  // データ保存画面のデータ名
      memo: '',                                                                      // データ保存画面のメモ
      modal_requirement_139: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
      language: state => state.systems.language,
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    },
    modalSize() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'md';
        default:
          return 'lg';
      }
    },
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  components: {
    "custom-table": customTable,
    "project-data": projectData,
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // ECUキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.i18nWsDidKey = `wsDid${this.systemId}`;
      this.modal_requirement_139 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_139 = false;
    },

    /** 作動履歴データ読み出し処理 */
    async readOperationHistory() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED: {
              // 正常終了：一覧表示画面へ遷移
              this.tableDataConvert(response.data.user_data.operation_histories);
              // requestIdを保持
              this.requestId = response.data.user_data.request_id;
              // プロジェクトデータ保存画面表示用データ名初期値の設定
              this.dataName = response.data.user_data.data_name;
              break;
            }
            case REQUEST_STATUS_FAILED:
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    // 一覧表作成
    tableDataConvert(operationHistories) {

      // テーブルの列定義
      this.tableColumns = [
        { name: "did", label: this.$t("message.header_vehice_ffd_item") },
        { name: "unit", label: this.$t("message.header_vehice_ffd_unit") }
      ];

      const cntHistory = this.$t('individual.label_table_header_history');
      for (let i = 1; i <= 3; i++) {
        this.tableColumns.push({
          name: `history_${i}`,
          label: `${cntHistory}${i}`
        });
      }

      // データ変換とテーブル表示データに追加
      const convertedData = operationHistories.map(history => {
        return {
          did: this.$t(`${this.i18nWsDidKey}.${history.did}`),
          unit: this.$t(`didUnit.${history.unit}`),
          history_1: history.data_type === 'YYYYMMDDhhmmss' ? this.convertLocalDateTime(history.data_records[0]) : history.data_records[0],
          history_2: history.data_type === 'YYYYMMDDhhmmss' ? this.convertLocalDateTime(history.data_records[1]) : history.data_records[1],
          history_3: history.data_type === 'YYYYMMDDhhmmss' ? this.convertLocalDateTime(history.data_records[2]) : history.data_records[2],
        };
      });

      this.$nextTick(() => {
        this.displayInfo = convertedData;
      });
    },

    /**
    * GMTからLocalTimeへ変換
    * @param {*} responseDate 絶対時間タイプスタンプ情報
    */
    convertLocalDateTime(responseDate) {
      if (!responseDate) {
        return '-';
      }

      const globalDate = new Date(responseDate);
      // 日付として無効な文字列の場合Backendから帰ってきた文字列をそのまま表示する
      if (globalDate.toString() === 'Invalid Date') {
        return responseDate;
      }

      // LocalTimeに変換
      return util.timeConverter(globalDate);
    },

    /** 保存ボタン押下 プロジェクト保存画面遷移 */
    screenMovement() {
      const vehicle = this.vehicleInfo.vehicle;
      // プロジェクト保存画面に必要な情報を設定
      this.vin = vehicle.vin.vin;
      this.displayProjectID = vehicle.project.id;

      // 車種情報から表示名称を取得
      const spec = shareVehicle.createModelDisplaySpec(vehicle.vin.type, vehicle.spec);
      this.vehicleName = shareVehicle.createModelDisplayNameCurrentLanguage(this.vehicleInfo, this.vehicleModelDisplayNameList, this.language, '{0} {1}', spec);
      // 海外VINの場合、登録番号は画面から非表示にする
      if (this.hideRegistrationNumber) {
        if (vinInfoUtils.outputRegistrationNumber(vehicle.vin)) {
          this.registrationNumber = vinInfoUtils.createRegistrationNumber(vehicle.vin);
        }
        else {
          this.registrationNumber = "";
        }
      }
      this.phase = PHASE.PROJECT_DATA;
    },

    async callSaveEvent() {
      if (this.$refs.projectData.validate()) {
        // プロジェクトデータ保存・更新処理
        if (await this.saveUpdate()) {
          await this.closeWizard();
        }
      }
    },

    /** プロジェクトデータ保存・更新処理 */
    async saveUpdate() {
      try {
        this.isLoading = true;

        let endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id
          + '/netApps/' + this.netAppId
          + '/requests/' + this.requestId;

        // 更新準備 API
        const saveRes = await apiService.postCall(apiService.WSP, endPoint, {}, this.operationScreen);

        let ret = false;
        // APIレスポンスの検証
        handler.validate(handler.validateTypes.all, saveRes, this, null, () => {
          ret = true;
        }, (result) => {
          // レスポンス不正時のコールバック
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });

        if (ret) {
          ret = false;
          endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id
            + '/' + saveRes.data.measured_id;
          const reqBody = {
            comment: this.$refs.projectData.inputDiagnoseComment,
            measured: {
              name: this.$refs.projectData.inputDataName,
              memo: this.$refs.projectData.inputMemo
            }
          };
          // 更新 API
          const updateRes = await apiService.postCall(apiService.WSP, endPoint, reqBody, this.operationScreen);
          // APIレスポンスの検証
          handler.validate(handler.validateTypes.all, updateRes, this, null, () => {
            ret = true;
          }, (result) => {
            // レスポンス不正時のコールバック
            if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
          });
        }
        return ret;
      }
      finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
    // 作動履歴データ読み出し処理
    this.readOperationHistory();
  },
};